import React, { useEffect, useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import { supabase } from '../../supabaseClient'
import Header from '../../components/Header'
import Swal from 'sweetalert2'
import { useBarista } from '../../contexts/BaristaContext'
import BaristaLoginModal from '../../components/BaristaLoginModal'

import { offlineDB } from '../../offline/db'
import { warmStaffSession } from '../../offline/posOfflineHelpers'

import Analytics from './Analytics'
import InventoryManagement from './InventoryManagement'
import OrderManagement from './OrderManagement'
import CustomerData from './CustomerData'
import LoyaltyRewardsStaff from './LoyaltyRewardsStaff'
import POSLayout from './POSLayout'
import Profile from '../Profile'
import Settings2 from '../Settings2'
import StaffSidebar from '../StaffSidebar'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import StaffManagementStaff from './StaffManagementStaff'
import ShiftAttendance from './ShiftAttendance'
import ShiftManagement from './ShiftManagement'
import StaffAnalytics from './StaffAnalytics'
import TransactionPrinter from './TransactionPrinter'

import AnalyticsSummary from './DashboardWidgets/AnalyticsSummary'
import OrdersSummary from './DashboardWidgets/OrderSummary'
import CustomerSummary from './DashboardWidgets/CustomerSummary'
import InventorySummary from './DashboardWidgets/InventorySummary'
import StaffMenuManagement from './StaffMenuManagement'
import AdminBranchManagement from './AdminBranchManagement'
import { DateRangeBar, TopSellingItems, SalesTooltip, formatCurrency, formatShortCurrency, presetStart, formatTimeAgo, getStatusColor, SkeletonBlock, PaymentMethodsBreakdown, normalizePaymentMethod } from '../Admin/DashboardOverview'

import { useProfile } from '../../contexts/ProfileContext'
import { getAuthItem } from '../../utils/auth'
import { useNavigate } from 'react-router-dom'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

export default function StaffDashboard() {
    const [staffData, setStaffData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [showLogout, setShowLogout] = useState(false)
    const [showLogoutModal, setShowLogoutModal] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const [activeMenu, setActiveMenuState] = useState(searchParams.get('section') || 'Dashboard Overview')
    const [profileChecked, setProfileChecked] = useState(false)

    const setActiveMenu = (menu) => {
        setActiveMenuState(menu)
        setSearchParams({ section: menu })
    }

    // keep state in sync when the user uses browser Back/Forward
    useEffect(() => {
        const urlMenu = searchParams.get('section')
        if (urlMenu && urlMenu !== activeMenu) setActiveMenuState(urlMenu)
    }, [searchParams])

    useDocumentTitle(activeMenu === 'POS System' ? 'POS' : activeMenu)
    const [newOrderNotifs, setNewOrderNotifs] = useState([])
    const [inventoryStats, setInventoryStats] = useState({ totalItems: 0, lowStockCount: 0, outOfStockCount: 0 })
    const [inventoryLoading, setInventoryLoading] = useState(false)
    const [salesStats, setSalesStats] = useState({ todaySales: 0, salesGrowth: 0, totalOrders: 0, ordersGrowth: 0, activeCustomers: 0, customersGrowth: 0 })
    const [refreshing, setRefreshing] = useState(false)
    const [allOrders, setAllOrders] = useState([])
    const [allOrderItems, setAllOrderItems] = useState([])
    const [recentOrders, setRecentOrders] = useState([])
    const [transactions, setTransactions] = useState([])

    const [preset, setPreset] = useState('30d')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [appliedCustomStart, setAppliedCustomStart] = useState('')
    const [appliedCustomEnd, setAppliedCustomEnd] = useState('')
    const [chartPeriod, setChartPeriod] = useState('day')

    const hasCustom = appliedCustomStart && appliedCustomEnd

    const dateStart = useMemo(() => {
        if (hasCustom) { const d = new Date(appliedCustomStart); d.setHours(0, 0, 0, 0); return d }
        return presetStart(preset)
    }, [hasCustom, appliedCustomStart, preset])

    const dateEnd = useMemo(() => {
        if (hasCustom) { const d = new Date(appliedCustomEnd); d.setHours(23, 59, 59, 999); return d }
        return new Date()
    }, [hasCustom, appliedCustomEnd])

    const periodRangeLabel = hasCustom
        ? `${customStart} → ${customEnd}`
        : { '1d': 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days' }[preset] ?? 'Custom'

    const goToFilteredMenu = (menuName) => {
        try {
            const toDateStr = (d) => {
                const y = d.getFullYear()
                const m = String(d.getMonth() + 1).padStart(2, '0')
                const day = String(d.getDate()).padStart(2, '0')
                return `${y}-${m}-${day}`
            }
            sessionStorage.setItem('staffDashboardFilterHandoff', JSON.stringify({
                preset: hasCustom ? 'custom' : preset,
                customStart: toDateStr(dateStart),
                customEnd: toDateStr(dateEnd),
            }))
            if (menuName === 'Order Management') {
                window.dispatchEvent(new CustomEvent('kaperk:orders-filter-handoff'))
            }
        } catch (err) {
            console.error('Failed to store staff dashboard filter handoff:', err)
        }
        setActiveMenu(menuName)
    }

    const paymentStats = useMemo(() => {
        const paid = transactions.filter(t => ['completed', 'paid'].includes(t.status))
        if (!paid.length) return { methods: [], top: null, totalAmount: 0, totalCount: 0 }
        const map = {}
        paid.forEach(t => {
            const key = normalizePaymentMethod(t.payment_method)
            if (!map[key]) map[key] = { method: key, count: 0, amount: 0 }
            map[key].count += 1
            map[key].amount += parseFloat(t.transaction_amount || 0)
        })
        const methods = Object.values(map).sort((a, b) => b.count - a.count)
        const totalCount = methods.reduce((s, m) => s + m.count, 0)
        const totalAmount = methods.reduce((s, m) => s + m.amount, 0)
        return { methods, top: methods[0], totalAmount, totalCount }
    }, [transactions])

    const chartData = useMemo(() => {
        if (!allOrders.length) return []

        if (chartPeriod === 'day') {
            const hours = Array.from({ length: 24 }, (_, i) => ({
                label: i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`,
                sales: 0, orders: 0
            }))
            allOrders.forEach(o => {
                const d = new Date(o.created_at)
                if (d >= dateStart && d <= dateEnd) {
                    hours[d.getHours()].orders += 1
                    if (o.order_status === 'completed') hours[d.getHours()].sales += parseFloat(o.final_amount || 0)
                }
            })
            return hours
        }

        // 'month' view — group by calendar date across the selected range
        const dayMap = {}
        allOrders.forEach(o => {
            const d = new Date(o.created_at)
            if (d < dateStart || d > dateEnd) return
            const key = d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })
            if (!dayMap[key]) dayMap[key] = { label: key, sales: 0, orders: 0, _t: d.getTime() }
            dayMap[key].orders += 1
            if (o.order_status === 'completed') dayMap[key].sales += parseFloat(o.final_amount || 0)
        })
        return Object.values(dayMap).sort((a, b) => a._t - b._t)
    }, [allOrders, dateStart, dateEnd, chartPeriod])

    const [btDevice, setBtDevice] = useState(null)
    const [btChar, setBtChar] = useState(null)
    const [btStatus, setBtStatus] = useState('disconnected')
    const [btPrinterName, setBtPrinterName] = useState(() => localStorage.getItem('kp_bt_printer_name') || '')
    const [stickerBtDevice, setStickerBtDevice] = useState(null)
    const [stickerBtChar, setStickerBtChar] = useState(null)
    const [stickerBtStatus, setStickerBtStatus] = useState('disconnected')
    const [stickerBtPrinterId, setStickerBtPrinterId] = useState(() => localStorage.getItem('kp_sticker_bt_printer_id') || '')
    const [stickerBtPrinterName, setStickerBtPrinterName] = useState(() => localStorage.getItem('kp_sticker_bt_printer_name') || '')
    const [printerMode, setPrinterMode] = useState(() => localStorage.getItem('kp_printer_mode') || 'system')

    const { profilePicture, adminUsername, adminRole } = useProfile()

    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
            .swal2-confirm { background-color: #A0826D !important; border: none !important; }
            .swal2-confirm:hover { background-color: #8B5A3C !important; }
            .swal2-cancel { background-color: #6b7280 !important; border: none !important; }
            .swal2-cancel:hover { background-color: #5a5a5a !important; }
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission()
        }
    }, [])

    const { selectedBarista, loadBaristas } = useBarista()

    useEffect(() => {
        if (staffData?.role === 'Barista' && staffData?.branch_id) {
            loadBaristas(staffData.branch_id)
        }
    }, [staffData?.role, staffData?.branch_id, loadBaristas])

    useEffect(() => {
        const fetchStaffData = async () => {
            const email = getAuthItem('userEmail')
            const userType = getAuthItem('userType')

            if (!email || userType !== 'staff') {
                window.location.href = '/login'
                return
            }

            if (!navigator.onLine) {
                try {
                    const cached = await offlineDB.metaCache.get('staffDashboardData')
                    if (!cached?.value) {
                        throw new Error('No cached staff data available yet. Please reconnect once before going offline.')
                    }
                    setStaffData(cached.value)
                } catch (err) {
                    setError(err.message || 'Failed to load staff data offline.')
                    console.error('Error loading cached staff data:', err)
                } finally {
                    setLoading(false)
                }
                return
            }

            try {
                const { data, error } = await supabase
                    .from('staff')
                    .select(`
                    id,
                    role,
                    permissions,
                    branch_id,
                    branches (
                        branch_id,
                        name
                    )
                `)
                    .or(`email.eq.${email},company_email.eq.${email}`)
                    .single()

                if (error) throw error

                let processedPermissions = data.permissions
                if (typeof data.permissions === 'string') {
                    try {
                        processedPermissions = JSON.parse(data.permissions)
                    } catch (parseError) {
                        console.error('Error parsing permissions:', parseError)
                        throw new Error('Invalid permissions format')
                    }
                }

                const fullStaffData = { ...data, permissions: processedPermissions }
                setStaffData(fullStaffData)

                // Cache so a refresh while offline can still boot the dashboard —
                // this is what lets the barista switcher / PIN modal even get a
                // chance to mount when there's no connection.
                await offlineDB.metaCache.put({ key: 'staffDashboardData', value: fullStaffData })

                // Warm every offline cache (menu, orders, baristas, admin PINs, etc.)
                // now that we know we're online and authenticated. This is the ONLY
                // place that currently calls warmStaffSession — awithout this call,
                // adminPinCache/baristaCache/etc. are never populated, no matter
                // how correct their underlying fetch functions are.
                warmStaffSession(true).catch(err =>
                    console.error('[StaffDashboard] warmStaffSession failed:', err)
                )

                const hasAnyPermission =
                    processedPermissions.canViewAnalytics ||
                    processedPermissions.canManageOrders ||
                    processedPermissions.canAccessCustomerData ||
                    processedPermissions.canModifyInventory

                if (!hasAnyPermission) console.warn('Staff has no permissions assigned')

            } catch (err) {
                setError('Failed to load staff data. Please try again.')
                console.error('Error fetching staff data:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchStaffData()
    }, [])

    useEffect(() => {
        if (staffData && !profileChecked) checkProfileCompletion()
    }, [staffData, profileChecked])

     // Jump to Inventory & POS if a notification (adjustment request / stock count) sent us here
    useEffect(() => {
        if (!staffData) return

        const canSeeInventory = staffData.permissions?.canModifyInventory

        const goToInventory = () => {
            if (canSeeInventory) setActiveMenu('Inventory Management')
        }

        // Case 1: page just mounted because of a redirect from another route
        if (sessionStorage.getItem('pendingInventoryTab')) {
            goToInventory()
        }

        // Case 2: already on this page, InventoryManagement not mounted yet
        // because activeMenu is something else — flip the menu, then let
        // InventoryManagement's own listener pick up the sub-tab on mount.
        const handleNavigateToSubTab = () => {
            goToInventory()
        }
        window.addEventListener('kaperk:navigate-inventory', handleNavigateToSubTab)
        return () => window.removeEventListener('kaperk:navigate-inventory', handleNavigateToSubTab)
    }, [staffData])

     // Jump to Shift Attendance if a remake notification sent us here — the
    // remake's reason/responsible-staff/status details live in ShiftAttendance's
    // Remake Accountability card, not in Order Management. The scroll target
    // (which section within the tab to scroll to) is left in sessionStorage
    // for ShiftAttendance itself to consume on mount.
    useEffect(() => {
        if (!staffData) return

        const goToAttendance = () => setActiveMenu('Shift Attendance')

        // Case 1: page just mounted because of a redirect from another route
        if (sessionStorage.getItem('pendingStaffTab') === 'attendance') {
            sessionStorage.removeItem('pendingStaffTab')
            goToAttendance()
        }

        // Case 2: already on this page — just flip the menu
        const handleNavigateToAttendance = () => {
            sessionStorage.removeItem('pendingStaffTab')
            goToAttendance()
        }
        window.addEventListener('kaperk:navigate-attendance', handleNavigateToAttendance)
        return () => window.removeEventListener('kaperk:navigate-attendance', handleNavigateToAttendance)
    }, [staffData])

    useEffect(() => { localStorage.setItem('kp_printer_mode', printerMode) }, [printerMode])
    useEffect(() => { localStorage.setItem('kp_bt_printer_name', btPrinterName) }, [btPrinterName])

    useEffect(() => {
        if (preset !== 'custom') { setAppliedCustomStart(''); setAppliedCustomEnd('') }
    }, [preset])

    useEffect(() => {
        const isValid = (s) => s?.length === 10 && !isNaN(new Date(s))
        if (isValid(customStart) && isValid(customEnd)) {
            setAppliedCustomStart(customStart)
            setAppliedCustomEnd(customEnd)
        }
    }, [customStart, customEnd])

    const checkProfileCompletion = async () => {
        try {
            const userEmail = getAuthItem('userEmail')
            const userType = getAuthItem('userType')

            if (!userEmail || userType !== 'staff') {
                setProfileChecked(true)
                return
            }

            const { data: staffInfo } = await supabase
                .from('staff')
                .select('*')
                .or(`email.eq.${userEmail},company_email.eq.${userEmail}`)
                .single()

            if (!staffInfo) { setProfileChecked(true); return }

            const { data: profileData } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', staffInfo.id)
                .eq('user_type', 'staff')
                .maybeSingle()

            const missingFields = []
            if (!profileData?.first_name) missingFields.push('First Name')
            if (!profileData?.last_name) missingFields.push('Last Name')
            if (!profileData?.phone_number) missingFields.push('Phone Number')
            if (!profileData?.address) missingFields.push('Address')

            if (missingFields.length > 0) {
                setTimeout(() => {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Profile Incomplete',
                        html: `
                            <div style="text-align:left;">
                                <p style="margin-bottom:15px;font-weight:500;">Please complete your profile to continue using the system.</p>
                                <p style="margin-bottom:10px;font-weight:500;">Missing information:</p>
                                <ul style="padding-left:20px;margin-bottom:15px;color:#DC2626;">
                                    ${missingFields.map(f => `<li style="margin-bottom:5px;">${f}</li>`).join('')}
                                </ul>
                                <p style="font-size:14px;color:#666;border-top:1px solid #E5E7EB;padding-top:12px;margin-top:12px;">
                                    Click <strong>Complete Profile</strong> to fill in the required information now, or <strong>Remind Me Later</strong> to do it another time.
                                </p>
                            </div>`,
                        confirmButtonColor: '#A0826D',
                        confirmButtonText: 'Complete Profile Now',
                        showCancelButton: true,
                        cancelButtonColor: '#6B7280',
                        cancelButtonText: 'Remind Me Later',
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    }).then(result => {
                        if (result.isConfirmed) setActiveMenu('My Profile')
                    })
                }, 1000)
            }

            setProfileChecked(true)
        } catch (err) {
            console.error('Error in profile check:', err)
            setProfileChecked(true)
        }
    }

    useEffect(() => {
        if (staffData?.permissions?.canModifyInventory) fetchInventoryOverview()
    }, [staffData])

    useEffect(() => {
        if (staffData?.role === 'Admin') fetchSalesOverview()
    }, [staffData, dateStart, dateEnd])

    const fetchInventoryOverview = async () => {
        try {
            setInventoryLoading(true)
            const { data, error } = await supabase
                .schema('inventory')
                .from('branch_inventory')
                .select('id, current_stock, reorder_level, is_active')
                .eq('branch_id', staffData.branch_id)
                .eq('is_active', true)
            if (error) throw error

            const lowStockCount = (data || []).filter(i => i.current_stock > 0 && i.current_stock <= i.reorder_level).length
            const outOfStockCount = (data || []).filter(i => i.current_stock <= 0).length

            setInventoryStats({ totalItems: data?.length || 0, lowStockCount, outOfStockCount })
        } catch (err) {
            console.error('Error fetching inventory overview:', err)
        } finally {
            setInventoryLoading(false)
        }
    }

    const fetchSalesOverview = async () => {
        try {
            setRefreshing(true)
            const { data: ordersData, error: ordersError } = await supabase
                .from('orders')
                .select('*')
                .eq('branch_id', staffData.branch_id)
                .gte('created_at', dateStart.toISOString())
                .lte('created_at', dateEnd.toISOString())
                .order('created_at', { ascending: false })
            if (ordersError) throw ordersError
            setAllOrders(ordersData || [])

            const allOrderIds = (ordersData || []).map(o => o.order_id)
            if (allOrderIds.length > 0) {
                const { data: txData, error: txError } = await supabase
                    .from('order_transactions')
                    .select('order_id, payment_method, transaction_amount, status, created_at')
                    .in('order_id', allOrderIds)
                if (txError) throw txError
                setTransactions(txData || [])
            } else {
                setTransactions([])
            }

            const recentList = (ordersData || []).slice(0, 5)
            const ordersWithItems = await Promise.all(recentList.map(async order => {
                const { data: items } = await supabase
                    .from('order_items')
                    .select('quantity, menu_items(name)')
                    .eq('order_id', order.order_id)
                return { ...order, items: items || [] }
            }))
            setRecentOrders(ordersWithItems)

            const completedOrderIds = (ordersData || [])
                .filter(o => o.order_status === 'completed')
                .map(o => o.order_id)

            if (completedOrderIds.length > 0) {
                const { data: itemsData, error: itemsError } = await supabase
                    .from('order_items')
                    .select(`order_id, item_id, quantity, item_price, menu_items(item_id, name, category, item_img, item_price)`)
                    .in('order_id', completedOrderIds)
                if (itemsError) throw itemsError
                setAllOrderItems(itemsData || [])
            } else {
                setAllOrderItems([])
            }

            const completedOrders = (ordersData || []).filter(o => o.order_status === 'completed')
            const todaySales = completedOrders.reduce((s, o) => s + parseFloat(o.final_amount || 0), 0)
            const totalOrders = completedOrders.length

            const rangeLengthMs = dateEnd - dateStart
            const prevStart = new Date(dateStart.getTime() - rangeLengthMs)
            const prevEnd = new Date(dateStart)

            const { data: prevOrders } = await supabase
                .from('orders')
                .select('final_amount, order_status, user_id')
                .eq('branch_id', staffData.branch_id)
                .gte('created_at', prevStart.toISOString())
                .lte('created_at', prevEnd.toISOString())

            const prevCompleted = (prevOrders || []).filter(o => o.order_status === 'completed')
            const prevSales = prevCompleted.reduce((s, o) => s + parseFloat(o.final_amount || 0), 0)
            const salesGrowth = prevSales > 0 ? ((todaySales - prevSales) / prevSales) * 100 : 0
            const ordersGrowth = prevCompleted.length > 0 ? ((totalOrders - prevCompleted.length) / prevCompleted.length) * 100 : 0

            const activeCustomers = new Set(
                completedOrders.filter(o => o.user_id).map(o => o.user_id)
            ).size

            const prevCustomers = new Set(
                (prevCompleted || []).filter(o => o.user_id).map(o => o.user_id)
            ).size

            const customersGrowth = prevCustomers > 0 ? ((activeCustomers - prevCustomers) / prevCustomers) * 100 : 0

            setSalesStats({ todaySales, salesGrowth, totalOrders, ordersGrowth, activeCustomers, customersGrowth })
        } catch (err) {
            console.error('Error fetching sales overview:', err)
        } finally {
            setRefreshing(false)
        }
    }

    const handleNewOrder = (order) => {
        setNewOrderNotifs(prev => [order, ...prev].slice(0, 10)) // keep last 10
    }

    const handleLogoutClick = () => { setShowLogout(false); setShowLogoutModal(true) }
    const handleConfirmLogout = () => { sessionStorage.clear(); localStorage.clear(); window.location.href = '/login' }
    const handleCancelLogout = () => setShowLogoutModal(false)
    const handleProfileClick = () => { setShowLogout(false); setActiveMenu('My Profile') }
    const handleSettingsClick = () => { setShowLogout(false); setActiveMenu('Settings') }

    const renderContent = () => {
        if (!staffData) return null
        const { permissions, role } = staffData

        switch (activeMenu) {

            case 'Dashboard Overview':
                return (
                    <div className="flex flex-col gap-5">
                        {/* Welcome banner */}
                        {/* <div className="bg-[#fff] rounded-xl p-5 text-black shadow-sm">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <h2 className="text-lg font-semibold">
                                        Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'},&nbsp;
                                        {staffData.role === 'Barista' && selectedBarista ? selectedBarista.first_name : (adminUsername || first_name)}! 
                                    </h2>
                                    <p className="text-black text-sm mt-0.5">
                                        Here's a quick snapshot of {staffData?.branches?.name || 'your branch'} today.
                                    </p>
                                </div>
                                <p className="text-sm text-black">
                                    {new Date().toLocaleDateString('en-PH', { weekday: 'long', month: 'long', day: 'numeric' })}
                                </p>
                            </div>
                        </div> */}

                        {staffData.role === 'Admin' ? (
                            <>
                                 <div className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur-sm -mx-6 px-6 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <span className="inline-flex items-center gap-1.5 text-xs text-[#A0826D] bg-orange-50 border border-orange-100 px-3 py-1 rounded-full w-fit">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                        {periodRangeLabel}
                                    </span>

                                    <DateRangeBar
                                        preset={preset} setPreset={setPreset}
                                        customStart={customStart} setCustomStart={setCustomStart}
                                        customEnd={customEnd} setCustomEnd={setCustomEnd}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => goToFilteredMenu('Business Analytics')}>
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center">
                                                <span className="text-green-600 font-bold text-xl leading-none">₱</span>
                                            </div>
                                            {refreshing ? (
                                                <SkeletonBlock className="w-12 h-5" />
                                            ) : (
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${salesStats.salesGrowth >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                                    {salesStats.salesGrowth >= 0 ? '+' : ''}{salesStats.salesGrowth.toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mb-1">Total Sales</p>
                                        {refreshing ? (
                                            <SkeletonBlock className="w-32 h-7 mb-1" />
                                        ) : (
                                            <h2 className="text-2xl font-bold text-gray-800">{formatCurrency(salesStats.todaySales)}</h2>
                                        )}
                                        <p className="text-xs text-gray-400 mt-1">vs prior period</p>
                                    </div>

                                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => goToFilteredMenu('Order Management')}>
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" className="text-blue-600">
                                                    <path fill="currentColor" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2s-.9-2-2-2M1 2v2h2l3.6 7.59l-1.35 2.45c-.16.28-.25.61-.25.96c0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12l.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2s2-.9 2-2s-.9-2-2-2" />
                                                </svg>
                                            </div>
                                            {refreshing ? (
                                                <SkeletonBlock className="w-12 h-5" />
                                            ) : (
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${salesStats.ordersGrowth >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                                    {salesStats.ordersGrowth >= 0 ? '+' : ''}{salesStats.ordersGrowth.toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mb-1">Completed Orders</p>
                                        {refreshing ? (
                                            <SkeletonBlock className="w-32 h-7 mb-1" />
                                        ) : (
                                            <h2 className="text-2xl font-bold text-gray-800">{salesStats.totalOrders}</h2>
                                        )}
                                        <p className="text-xs text-gray-400 mt-1">vs prior period</p>
                                    </div>

                                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => setActiveMenu('Inventory Management')}>
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="w-11 h-11 bg-orange-50 rounded-xl flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" className="text-orange-600">
                                                    <path fill="currentColor" d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2a3 3 0 0 0 6 0h6a3 3 0 0 0 6 0h2v-5zM6 18.5A1.5 1.5 0 1 1 7.5 17A1.5 1.5 0 0 1 6 18.5m13.5-9l1.96 3H17V9.5zM18 18.5a1.5 1.5 0 1 1 1.5-1.5a1.5 1.5 0 0 1-1.5 1.5" />
                                                </svg>
                                            </div>
                                            <div className="flex flex-col items-end gap-1">
                                                {inventoryLoading ? (
                                                    <SkeletonBlock className="w-20 h-5" />
                                                ) : (
                                                    <>
                                                        {inventoryStats.outOfStockCount > 0 && (
                                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-red-600 bg-red-50">
                                                                {inventoryStats.outOfStockCount} out of stock
                                                            </span>
                                                        )}
                                                        {inventoryStats.lowStockCount > 0 && (
                                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-amber-600 bg-amber-50">
                                                                {inventoryStats.lowStockCount} low stock
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-1">Inventory Overview</p>
                                        {inventoryLoading ? (
                                            <SkeletonBlock className="w-32 h-7 mb-1" />
                                        ) : (
                                            <h2 className="text-2xl font-bold text-gray-800">{inventoryStats.totalItems} items</h2>
                                        )}
                                        <p className="text-xs text-gray-400 mt-1">Click to manage stock</p>
                                    </div>

                                    <div
                                        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => setActiveMenu('Customer Insight')}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="w-11 h-11 bg-purple-50 rounded-xl flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" className="text-purple-600">
                                                    <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3s1.34 3 3 3m-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5S5 6.34 5 8s1.34 3 3 3m0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5m8 0c-.29 0-.62.02-.97.05c1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5" />
                                                </svg>
                                            </div>
                                            {refreshing ? (
                                                <SkeletonBlock className="w-12 h-5" />
                                            ) : (
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${salesStats.customersGrowth >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                                    {salesStats.customersGrowth >= 0 ? '+' : ''}{salesStats.customersGrowth.toFixed(1)}%
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mb-1">Active Customers</p>
                                        {refreshing ? (
                                            <SkeletonBlock className="w-32 h-7 mb-1" />
                                        ) : (
                                            <h2 className="text-2xl font-bold text-gray-800">{salesStats.activeCustomers}</h2>
                                        )}
                                        <p className="text-xs text-gray-400 mt-1">vs prior period</p>
                                    </div>

                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                                    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                                        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
                                            <div>
                                                <h2 className="text-lg font-bold text-gray-800">Sales Overview</h2>
                                                <p className="text-sm text-gray-500 mt-0.5">
                                                    {staffData?.branches?.name || 'Your branch'}
                                                    {' · '}{periodRangeLabel}
                                                </p>
                                            </div>
                                            <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
                                                {['day', 'month'].map(p => (
                                                    <button
                                                        key={p}
                                                        onClick={() => setChartPeriod(p)}
                                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                                            chartPeriod === p ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                                        }`}
                                                    >
                                                        {p === 'day' ? 'By Hour' : 'By Day'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="p-6 flex flex-col flex-1">
                                            {refreshing ? (
                                                <SkeletonBlock className="w-full" style={{ minHeight: 400 }} />
                                            ) : chartData.length === 0 ? (
                                                <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                                                    No sales data for this period
                                                </div>
                                            ) : (
                                                <div className="w-full" style={{ minHeight: 400 }}>
                                                    <ResponsiveContainer debounce={200} width="100%" height="100%">
                                                        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                                            <defs>
                                                                <linearGradient id="staffSalesGradient" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#A0826D" stopOpacity={0.2} />
                                                                    <stop offset="95%" stopColor="#A0826D" stopOpacity={0} />
                                                                </linearGradient>
                                                                <linearGradient id="staffOrdersGradient" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.15} />
                                                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={2} />
                                                            <YAxis yAxisId="sales" orientation="left" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={formatShortCurrency} width={60} />
                                                            <YAxis yAxisId="orders" orientation="right" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={35} />
                                                            <Tooltip content={<SalesTooltip />} />
                                                            <Area yAxisId="sales" type="monotone" dataKey="sales" stroke="#A0826D" strokeWidth={2} fill="url(#staffSalesGradient)" dot={false} activeDot={{ r: 4, fill: '#A0826D', strokeWidth: 0 }} />
                                                            <Area yAxisId="orders" type="monotone" dataKey="orders" stroke="#3B82F6" strokeWidth={2} fill="url(#staffOrdersGradient)" dot={false} activeDot={{ r: 4, fill: '#3B82F6', strokeWidth: 0 }} />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                                                <span className="flex items-center gap-1.5">
                                                    <span className="w-3 h-3 rounded-sm" style={{ background: '#A0826D' }}></span>Sales (₱)
                                                </span>
                                                <span className="flex items-center gap-1.5">
                                                    <span className="w-3 h-3 rounded-sm" style={{ background: '#3B82F6' }}></span>Orders (count)
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <TopSellingItems
                                            allOrders={allOrders}
                                            allOrderItems={allOrderItems}
                                            dateStart={dateStart}
                                            dateEnd={dateEnd}
                                            refreshing={refreshing}
                                        />
                                    </div>
                                </div>

                                <PaymentMethodsBreakdown
                                    methods={paymentStats.methods}
                                    top={paymentStats.top}
                                    totalAmount={paymentStats.totalAmount}
                                    totalCount={paymentStats.totalCount}
                                    refreshing={refreshing}
                                    onViewAnalytics={() => setActiveMenu('Business Analytics')}
                                />

                                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                                    <div className="px-4 sm:px-6 py-5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-800">Recent Orders</h2>
                                            <p className="text-sm text-gray-500 mt-0.5">Latest 5 orders in selected period</p>
                                        </div>
                                        <span className="text-xs text-[#A0826D] font-medium bg-orange-50 px-3 py-1 rounded-full">
                                            {recentOrders.length} shown
                                        </span>
                                    </div>
                                    <div className="divide-y divide-gray-50">
                                        {refreshing ? (
                                            Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className="px-6 py-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1 space-y-2">
                                                            <SkeletonBlock className="h-4 w-1/4" />
                                                            <SkeletonBlock className="h-3 w-1/2" />
                                                        </div>
                                                        <div className="text-right ml-4 space-y-2">
                                                            <SkeletonBlock className="h-4 w-16 ml-auto" />
                                                            <SkeletonBlock className="h-3 w-12 ml-auto" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : recentOrders.length === 0 ? (
                                            <div className="px-4 sm:px-6 py-10 text-center text-gray-400 text-sm">No recent orders</div>
                                        ) : (
                                            recentOrders.map(order => (
                                                <div key={order.order_id} className="px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors">
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                                                <span className="font-semibold text-gray-800 text-sm">#{order.reference_number}</span>
                                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                                                                    {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-gray-500 break-words">
                                                                {order.items.map(item => `${item.quantity}x ${item.menu_items?.name || 'Unknown'}`).join(', ')}
                                                            </p>
                                                        </div>
                                                        <div className="text-left sm:text-right sm:ml-4 flex sm:block items-center justify-between sm:justify-start">
                                                            <p className="font-bold text-gray-800 text-sm">{formatCurrency(order.final_amount)}</p>
                                                            <p className="text-xs text-gray-400 mt-0.5 sm:mt-0.5">{formatTimeAgo(order.created_at)}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                             <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                {permissions.canViewAnalytics && staffData.role !== 'Barista' && (
                                    <AnalyticsSummary
                                        branchId={staffData.branch_id}
                                        onViewFull={() => setActiveMenu('Business Analytics')}
                                    />
                                )}
                                {permissions.canManageOrders && (
                                    <OrdersSummary
                                        branchId={staffData.branch_id}
                                        onViewFull={() => setActiveMenu('Order Management')}
                                    />
                                )}
                                {permissions.canAccessCustomerData && staffData.role !== 'Barista' && (
                                    <CustomerSummary
                                        onViewFull={() => setActiveMenu('Customer Insight')}
                                    />
                                )}
                                {permissions.canModifyInventory && (
                                    <InventorySummary
                                        branchId={staffData.branch_id}
                                        onViewFull={() => setActiveMenu('Inventory Management')}
                                    />
                                )}
                            </div>
                        )}

                        {!permissions.canViewAnalytics &&
                            !permissions.canManageOrders &&
                            !permissions.canAccessCustomerData &&
                            !permissions.canModifyInventory && (
                                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg shadow-sm p-12 text-center border-2 border-amber-200">
                                    <div className="max-w-md mx-auto">
                                        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                                                <path d="M12 8v4" /><path d="M12 16h.01" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-800 mb-3">Access Restricted</h3>
                                        <p className="text-gray-700 text-lg mb-2">You currently have no permissions assigned to your account.</p>
                                        <p className="text-gray-600 mb-6">Please contact your system administrator to request access to features.</p>
                                        <div className="bg-white rounded-lg p-4 border border-amber-200">
                                            <p className="text-sm text-gray-600 mb-2"><strong>Your Account Details:</strong></p>
                                            <p className="text-sm text-gray-700">
                                                Branch: {staffData.branches?.name || 'Unassigned'}<br />
                                                Role: {role}<br />
                                                Email: {sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail')}
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleLogoutClick}
                                            className="mt-6 px-6 py-2.5 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8B6F5F] transition-colors inline-flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path fill="currentColor" d="M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h6q.425 0 .713.288T12 4t-.288.713T11 5H5v14h6q.425 0 .713.288T12 20t-.288.713T11 21zm12.175-8H10q-.425 0-.712-.288T9 12t.288-.712T10 11h7.175L15.3 9.125q-.275-.275-.275-.675t.275-.7t.7-.313t.725.288L20.3 11.3q.3.3.3.7t-.3.7l-3.575 3.575q-.3.3-.712.288t-.713-.313q-.275-.3-.262-.712t.287-.688z" />
                                            </svg>
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                    </div>
                )

            case 'Business Analytics':
                return permissions.canViewAnalytics && staffData.role === 'Admin'
                    ? <Analytics staffRole={staffData.role} branchId={staffData.branch_id} />
                    : <div className="text-center p-8 text-gray-500">No access to this section</div>

            case 'Customer Insight':
                return permissions.canAccessCustomerData
                    ? <CustomerData />
                    : <div className="text-center p-8 text-gray-500">No access to this section</div>

            case 'Loyalty Management':
                return <LoyaltyRewardsStaff />

            case 'Menu Management':
                return staffData.role === 'Admin'
                    ? <StaffMenuManagement />
                    : <div className="text-center p-8 text-gray-500">No access to this section</div>

            case 'Order Management':
                return null

            case 'Inventory Management':
                return permissions.canModifyInventory
                    ? <InventoryManagement />
                    : <div className="text-center p-8 text-gray-500">No access to this section</div>

            case 'POS System':
                return null

            case 'Transaction Printer':
                return (
                    <TransactionPrinter
                        btDevice={btDevice} setBtDevice={setBtDevice}
                        btChar={btChar} setBtChar={setBtChar}
                        btStatus={btStatus} setBtStatus={setBtStatus}
                        btPrinterName={btPrinterName} setBtPrinterName={setBtPrinterName}
                        stickerBtDevice={stickerBtDevice} setStickerBtDevice={setStickerBtDevice}
                        stickerBtChar={stickerBtChar} setStickerBtChar={setStickerBtChar}
                        stickerBtStatus={stickerBtStatus} setStickerBtStatus={setStickerBtStatus}
                        stickerBtPrinterId={stickerBtPrinterId} setStickerBtPrinterId={setStickerBtPrinterId}
                        stickerBtPrinterName={stickerBtPrinterName} setStickerBtPrinterName={setStickerBtPrinterName}
                        printerMode={printerMode} setPrinterMode={setPrinterMode}
                    />
                )

            case 'Shift Attendance':
                return <ShiftAttendance staffData={staffData} />

            case 'Staff Management':
                return staffData.role === 'Admin'
                    ? <StaffManagementStaff adminStaffData={staffData} />
                    : <div className="text-center p-8 text-gray-500">No access to this section</div>

            case 'Branch Management':
                return staffData.role === 'Admin'
                    ? <AdminBranchManagement staffData={staffData} onGoToProfile={() => setActiveMenu('My Profile')} />
                    : <div className="text-center p-8 text-gray-500">No access to this section</div>

            case 'My Profile':
                return <Profile />

            case 'Settings':
                return <Settings2 />

            default:
                return <div className="text-center p-8 text-gray-500">Select a menu item</div>
        }
    }

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50 z-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A0826D] mx-auto" />
                    <p className="mt-4 text-gray-600">Loading dashboard...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50 z-50">
                <div className="text-center">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-4">
                        <p>{error}</p>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-[#FF6F3C] text-white px-6 py-2 rounded-lg hover:bg-[#ff4c0b] transition">
                        Retry
                    </button>
                </div>
            </div>
        )
    }
    if (staffData.role === 'Barista' && !selectedBarista) {
        return (
            <BaristaLoginModal allowCancel={false} onSuccess={() => { }} />
        )
    }

    const { role } = staffData

    return (
        <div className="flex h-screen bg-gray-50">
            {showLogoutModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                        onClick={handleCancelLogout}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
                        <div className="flex justify-center pt-8 pb-4">
                            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
                                    <path fill="#A0826D" d="M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h6q.425 0 .713.288T12 4t-.288.713T11 5H5v14h6q.425 0 .713.288T12 20t-.288.713T11 21zm12.175-8H10q-.425 0-.712-.288T9 12t.288-.712T10 11h7.175L15.3 9.125q-.275-.275-.275-.675t.275-.7t.7-.313t.725.288L20.3 11.3q.3.3.3.7t-.3.7l-3.575 3.575q-.3.3-.712.288t-.713-.313q-.275-.3-.262-.712t.287-.688z" />
                                </svg>
                            </div>
                        </div>
                        <div className="text-center px-6 pb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Logout Confirmation</h3>
                            <p className="text-gray-600">Are you sure you want to logout from Coffee Staff Portal?</p>
                        </div>
                        <div className="flex gap-3 px-6 pb-6">
                            <button onClick={handleCancelLogout}
                                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleConfirmLogout}
                                className="flex-1 px-4 py-2.5 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8B6F5F] transition-colors">
                                Yes, Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Sidebar ── */}
            <StaffSidebar
                staffData={staffData}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                showLogout={showLogout}
                setShowLogout={setShowLogout}
                activeMenu={activeMenu}
                setActiveMenu={setActiveMenu}
                onLogoutClick={handleLogoutClick}
            />



            {staffData.role === 'Barista' && (
                <div className={`fixed inset-0 z-40 bg-white ${activeMenu === 'POS System' ? '' : 'hidden'}`}>
                    <POSLayout
                        onBack={() => setActiveMenu('Dashboard Overview')}
                        btChar={btChar}
                        btStatus={btStatus}
                        btPrinterName={btPrinterName}
                        printerMode={printerMode}
                        isVisible={activeMenu === 'POS System'}
                    />
                </div>
            )}


            <div className="flex-1 overflow-y-auto">
                {activeMenu !== 'My Profile' && activeMenu !== 'Settings' && (
                    <div className="mb-2">
                        <Header
                            title={activeMenu}
                            subtitle="Overview and analytics for your coffee shop operations"
                            onGoToProfile={() => setActiveMenu('My Profile')}
                            newOrderNotifs={newOrderNotifs}
                            onClearOrderNotifs={() => setNewOrderNotifs([])}
                            onGoToOrders={() => setActiveMenu('Order Management')}
                        />
                    </div>
                )}

                {/* Order Management: always mounted, just hidden via CSS so its offline listeners stay alive */}
                <div className={activeMenu === 'Order Management' ? '' : 'hidden'}>
                    {staffData.permissions.canManageOrders && <OrderManagement onNewOrder={handleNewOrder} />}
                </div>

                <div className={activeMenu === 'Order Management' ? 'hidden' : ''}>
                    <div className="px-6 pb-6">
                        {renderContent()}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .sidebar-nav::-webkit-scrollbar { width: 6px; }
                .sidebar-nav::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 10px; }
                .sidebar-nav::-webkit-scrollbar-thumb { background: #A0826D; border-radius: 10px; }
                .sidebar-nav::-webkit-scrollbar-thumb:hover { background: #8B6F5F; }
                .sidebar-nav { scrollbar-width: thin; scrollbar-color: #474747 rgba(255,255,255,0.1); }
            `}</style>
        </div>
    )
}
